import { interpolateHsl as d3InterpolateHsl } from 'd3-interpolate'
import dayjs from 'dayjs'
import { calculateLPO } from 'features/fde'
import {
  AggregateHealthPlanComponentData,
  AssetHealthActionCostType,
  GeckoAssetHealthPlan,
  GeckoAssetModel,
  GeckoUnit,
  HealthPlanAction,
} from 'types'

export interface PlanPlotData {
  planName: string
  unitName?: string
  actionCount?: number
  preferred: boolean
  planId: string
  planCost: number
  planActions: HealthPlanAction[]
  planRaw: GeckoAssetHealthPlan
}

export interface HealthPlanDataForPlot {
  unitName: string
  components: Map<string, AggregateHealthPlanComponentData>
  criticality: string
  unitRaw?: GeckoUnit
  plans: PlanPlotData[]
  assetModel: GeckoAssetModel | undefined
  area: string
  preferredNextActionDate?: string
  preferredPlanCost?: number
  lastInspectionDate: string
}

const minColor = 'hsl(60, 65%, 70%)'
const maxColor = 'hsl(60, 100%, 100%)'

export interface ChartDataConfig {
  aggregatedData: HealthPlanDataForPlot[]
  onlyPreferred?: boolean
  groupKey?: 'label' | 'planName'
  stacked?: boolean
  allYears?: string[]
}

export type ProcessedChartData = Array<{
  label: string
  data: Array<{ x: string; y: number }>
  backgroundColor: string
  borderColor: string
  borderWidth: number
  stack?: string
}>

export const createCostDatasets = (
  config: ChartDataConfig
): ProcessedChartData => {
  const { aggregatedData, onlyPreferred = true, allYears = [] } = config

  // Process action costs with CAPEX/OPEX separation
  const actionCosts = aggregatedData.flatMap((unit) =>
    unit.plans
      .filter((plan) => !onlyPreferred || plan.preferred)
      .flatMap((plan) =>
        plan.planActions.map((entry) => ({
          cost: entry.actionData.costTotal ?? 0,
          date: dayjs(entry.action.data.actionDate).format('YYYY'),
          label: unit.unitName,
          planName: plan.planName,
          // If costTypeHierarchy is not defined, default to CAPEX
          costType:
            entry.action.data.costTypeHierarchy?.[0] === 'OPEX'
              ? AssetHealthActionCostType.OPEX
              : AssetHealthActionCostType.CAPEX,
        }))
      )
  )

  const datasets: ProcessedChartData = []
  const costTypes = [...new Set(actionCosts.map((p) => p.costType))].sort()

  const colorSchemes: Record<
    AssetHealthActionCostType,
    ReturnType<typeof d3InterpolateHsl>
  > = {
    [AssetHealthActionCostType.CAPEX]: d3InterpolateHsl(minColor, maxColor), // green/yellow
    [AssetHealthActionCostType.OPEX]: d3InterpolateHsl(
      'hsl(25, 65%, 60%)',
      'hsl(35, 80%, 75%)'
    ), // Orange
  }

  // Process each cost type
  costTypes.forEach((costType) => {
    const assetsForCostType = [
      ...new Set(
        actionCosts.filter((p) => p.costType === costType).map((p) => p.label)
      ),
    ].sort()

    const colorInterpolater =
      colorSchemes[costType] || d3InterpolateHsl(minColor, maxColor)

    assetsForCostType.forEach((assetName, i) => {
      const data = allYears.map((year) => {
        const costForYear = actionCosts
          .filter(
            (p) =>
              p.date === year &&
              p.costType === costType &&
              p.label === assetName
          )
          .reduce((sum, p) => sum + p.cost, 0)
        return {
          x: year,
          y: costForYear,
        }
      })

      datasets.push({
        label: `${costType.toUpperCase()} - ${assetName}`,
        data,
        backgroundColor: colorInterpolater(
          i / Math.max(assetsForCostType.length - 1, 1)
        ),
        borderColor: colorInterpolater(
          i / Math.max(assetsForCostType.length - 1, 1)
        ),
        borderWidth: 1,
        stack: costType.toLowerCase(),
      })
    })
  })

  return datasets
}

export const createLPODataSets = (
  config: ChartDataConfig
): ProcessedChartData => {
  const { aggregatedData, stacked = true, allYears = [] } = config
  // Group units by area
  const unitsByArea = aggregatedData.reduce<
    Record<string, HealthPlanDataForPlot[]>
  >((acc, unit) => {
    if (!unit.area) return acc
    if (!acc[unit.area]) {
      acc[unit.area] = []
    }
    acc[unit.area].push(unit)
    return acc
  }, {})

  // Calculate min health score for each unit within its area for each year
  const minHealthScoreByArea = Object.fromEntries(
    Object.entries(unitsByArea).map(([area, units]) => {
      const yearlyMinScores = allYears.map((year) => {
        const scoresWithUnits = units.flatMap((unit) =>
          Array.from(unit.components.values()).map((component) => ({
            score:
              component.preferredPlanDamageAreas?.[year]?.healthScore ?? 100,
            unit: unit,
            componentName: component.componentName,
          }))
        )

        const minScore = Math.min(...scoresWithUnits.map((s) => s.score))
        const minScoreEntry = scoresWithUnits.find((s) => s.score === minScore)

        return [
          year,
          {
            score: minScore,
            unit: minScoreEntry?.unit,
          },
        ] as const
      })
      return [area, Object.fromEntries(yearlyMinScores)] as const
    })
  )

  const colorInterpolater = d3InterpolateHsl(minColor, maxColor)
  const totalAreaLength = Object.keys(minHealthScoreByArea).length

  return Object.entries(minHealthScoreByArea).map(
    ([area, yearlyMinScores], i) => {
      const data = Object.entries(yearlyMinScores).map(
        ([year, minScoreEntry]) => {
          if (!minScoreEntry.unit) {
            return {
              x: year,
              y: 0,
            }
          }

          const lpoCost =
            calculateLPO({
              assetComponents: minScoreEntry.unit.components,
              year: dayjs(year),
              criticality: parseInt(minScoreEntry.unit.criticality),
              unit: minScoreEntry.unit.unitRaw,
              minimumHealthScore: minScoreEntry.score,
            }) ?? 0

          return {
            x: year,
            y: lpoCost,
          }
        }
      )

      return {
        label: `${area} LPO`,
        data,
        backgroundColor: colorInterpolater(i / totalAreaLength),
        borderColor: colorInterpolater(i / totalAreaLength),
        borderWidth: 1,
        stacked: stacked ? undefined : area,
      }
    }
  )
}

export const createHealthPlanPlotData = (
  aggregateData: HealthPlanDataForPlot[],
  yearsToDisplay: number[] = [],
  opts?: {
    onlyPreferred?: boolean
    groupKey?: 'label' | 'planName'
    stacked?: boolean
  }
) => {
  const allYears = yearsToDisplay.map((year) => year.toString())

  const costDataSet = createCostDatasets({
    aggregatedData: aggregateData,
    onlyPreferred: true,
    groupKey: 'label',
    stacked: true,
    allYears,
    ...opts,
  })

  const lpoGraphData = createLPODataSets({
    aggregatedData: aggregateData,
    onlyPreferred: true,
    groupKey: 'label',
    stacked: true,
    allYears,
    ...opts,
  })

  return {
    labels: allYears,
    datasets: [...costDataSet, ...lpoGraphData],
  }
}
